import {ResponseBody} from '@gongxigames/framework/integrations/rubyplay/types';
import {MessageButtonAction, Message, GlobalEventsMap, messages, injectable} from '@gongxigames/framework/slot';
import {consoleLogger, IEventManager} from '@gongxigames/framework/core';

export type ResponseBodyData = {
    code: string;
    message: string | any;
    displayOptions?: DisplayOptions;
};

export type ErrorBodyData = {
    data: ResponseBodyData;
    status: string;
    topic: string;
};

export type GameServerError = ResponseBody & ErrorBodyData;

export type DisplayOptions = {
    title: string;
    buttons: ButtonsData[];
};

export type ButtonsData = {
    action: ButtonActions;
    caption: string;
};

export type ButtonActions = 'accept' | 'restart' | 'deposit' | 'history' | 'quit';

export type ButtonsActionsMap = Record<ButtonActions, MessageButtonAction>;

const ServerToClientActions: ButtonsActionsMap = {
    deposit: 'deposit',
    history: 'history',
    quit: 'go_to_home',
    restart: 'reload',
    accept: 'close_message'
};

export type CustomErrorData = {
    message: string;
    title: string;
    buttons: string[];
};

export type ErrorCodeType = 'PA-2' | 'PA-3' | 'PA-4' | 'PA-6' | 'PA-13' | 
    'GS-2' | 'GS-3' | 'GS-4' | 'GS-5' | 'GS-7' | 'GS-10' | 'GS-15' | 'GS-16' | 
    'GS-17' | 'GS-18' | 'GS-28' | 'GS-95' | 'GS-96' | 'GS-97' | 'GS-12' | 'GS-13' | 
    'WALLET-1' | 'GS-98' | 'GS-99' | 'M-1' | 'M-4' | 'M-5';

// eslint-disable-next-line
export const messages_BY_ERROR_CODE: Record<ErrorCodeType, Message> = {
    'PA-2': messages.PLAYER_BLOCKED,
    'PA-3': messages.SESSION_EXPIRED,
    'PA-4': messages.GAME_DISABLED,
    'PA-6': messages.UNAVAILABLE_LOCATION,
    'PA-13': messages.UNRESOLVED_SESSION,
    'GS-2': messages.PLAYER_BLOCKED,
    'GS-3': messages.SESSION_EXPIRED,
    'GS-4': messages.GAME_DISABLED,
    'GS-5': messages.INSUFFICIENT_FUNDS,
    'GS-7': messages.SESSION_EXPIRED,
    'GS-10': messages.INSUFFICIENT_PROMOTION_BALANCE,
    'GS-15': messages.PROMOTION_TERMINATED_ERROR,
    'GS-16': messages.PROMOTION_OPTED_IN_WARNING,
    'GS-17': messages.PROMOTION_OPTED_OUT_WARNING,
    'GS-18': messages.PROMOTION_COMPLETED_WARNING,
    'GS-28': messages.SESSION_TIME_LIMIT_REACHED,
    'GS-95': messages.CUSTOM,
    'GS-96': messages.CUSTOM,
    'GS-97': messages.CUSTOM,
    'GS-12': messages.BROKEN_ROUND,
    'GS-13': messages.BROKEN_ROUND,
    'WALLET-1': messages.BROKEN_ROUND,
    'GS-98': messages.NOT_CRITICAL_CUSTOM_MESSAGE,
    'GS-99': messages.CRITICAL_CUSTOM_MESSAGE,
    'M-1': messages.GAME_UNAVAILABLE,
    'M-4': messages.BET_PLACE,
    'M-5': messages.GAME_UNAVAILABLE
};
export const CUSTOM_SERVER_ERROR_CODE = 'GS-95';
export const CUSTOM_NOT_CRITICAL_ERROR_CODE = 'GS-98';
export const CUSTOM_ERROR_CODE = 'GS-97';
const SESSION_EXPIRED_CODE = 'GS-7';

@injectable()
export class RubyPlayErrorsHandler {
    protected _eventManager: IEventManager;
    protected _gameServerError: GameServerError | undefined;

    constructor(eventManager: IEventManager) {
        this._eventManager = eventManager;
    }

    public handleGameServerError(error: GameServerError): void {
        this._gameServerError = error;

        this.sendMessageError(this.getMessageError());

        consoleLogger.error(error);
    }

    protected getEventManager<E extends GlobalEventsMap>(): IEventManager<E> {
        return this._eventManager;
    }

    // TODO: should be changed during task for creating custom logic error (with customizing title, message and buttons)
    // also will be reduced amount of specific error codes as well as logic based on
    // hasCustomMessage, hasCustomTitle, hasCustomButtons parameters
    protected getMessageError(): Message {
        const DEFAULT_ERROR = {code: '', message: ''};
        const {code, message} = this._gameServerError ? this._gameServerError.data : DEFAULT_ERROR;
        const errorMessage = this.getMessageByCode(code as ErrorCodeType);

        if (this.isCustomError(code)) {
            const errorData = this.getCustomErrorData(message);

            if (errorMessage.title === '' && errorData.title) {
                errorMessage.title = errorData.title;
            }

            if (errorMessage.message === '' && errorData.message) {
                errorMessage.message = errorData.message;
            }

            if (errorData.buttons.length) {
                errorMessage.buttons = errorData.buttons.map(title => {
                    return {title, action: 'none'};
                });
            }
        } else if (this.isCustomServerError(code)) {
            const {displayOptions} = (this._gameServerError as ErrorBodyData)!.data;

            errorMessage.message = message;

            if (displayOptions) {
                errorMessage.title = displayOptions.title;
                errorMessage.buttons = displayOptions.buttons.map(({action, caption}) => ({
                    action: ServerToClientActions[action],
                    title: caption
                }));
            }
        }

        if (errorMessage && errorMessage.message === '') {
            errorMessage.message = message;
        }

        return errorMessage || messages.INTERNAL_ERROR;
    }

    public handleGameServerInfo(info: GameServerError, callback?: () => void): void {
        let infoMessage = this.getMessageByCode(info?.data?.code as ErrorCodeType);
        if (!infoMessage) {
            infoMessage = { ...messages.CONNECTION_ERROR };
        } 

        if (info?.data?.code === 'GS-98') {
            infoMessage = { ...messages.PROMOTION_TERMINATED_WARNING };
        }

        if (infoMessage.buttons && infoMessage.buttons.length > 0 && callback) {
            infoMessage.buttons[0].actionCallback = callback;
        }

        this.sendMessageError(infoMessage);
    }

    protected isCustomError(code: string): boolean {
        return code === CUSTOM_ERROR_CODE;
    }

    protected isCustomServerError(code: string): boolean {
        return code === CUSTOM_SERVER_ERROR_CODE;
    }

    protected getMessageByCode(errorCode: ErrorCodeType): Message {
        return messages_BY_ERROR_CODE[errorCode];
    }

    protected getCustomErrorData(data: any): CustomErrorData {
        // for now custom error can be send only by Relax, so this method is overridden in RelaxErrorHandler
        // need to implement when agreed the custom error data interface that passes into this function
        return {message: '', title: '', buttons: []};
    }

    public handleInitSessionError(error: any): void {
        const errorMessage = this.getMessageByCode(error.errorCode as ErrorCodeType);

        if (errorMessage) {
            this.sendMessageError(errorMessage);
        } else {
            this.sendMessageError(messages.INTERNAL_ERROR);
        }

        consoleLogger.error(error);
    }

    public handleRequestError(xhr: XMLHttpRequest, isTimeout: boolean): void {
        const errorMessage = this.getMessageByCode(xhr?.response?.data?.code as ErrorCodeType);

        if (errorMessage) {
            this.sendMessageError(errorMessage);
        } else {
            if (isTimeout) {
                this.sendMessageError(messages.CONNECTION_TIMEOUT);
            } else {
                this.sendMessageError(messages.CONNECTION_ERROR);
            }
        }

        consoleLogger.error(xhr);
    }

    public handleSlowConnection(): void {
        this.sendMessageError(messages.SLOW_CONNECTION);
    }

    public closeSlowConnection(): void {
        this.getEventManager().emit('General.Message.CLOSE', messages.SLOW_CONNECTION);
    }

    public isSessionExpired(): boolean {
        return this._gameServerError !== undefined && this._gameServerError!.data.code === SESSION_EXPIRED_CODE;
    }

    protected sendMessageError(message: Message): void {
        this.getEventManager().emit('General.Message.SHOW', message);
    }

    private getServerError(): {message: string; code: string;} {
        const DEFAULT_ERROR = {code: '', message: ''};

        return this._gameServerError ? this._gameServerError.data : DEFAULT_ERROR;
    }
}
